import { createAsyncThunk, createSlice, unwrapResult } from '@reduxjs/toolkit'
import _ from 'lodash'
import { sendAsync } from '../../../app/applicationSlice'
import { RootDispatch, RootState, RootThunkApi } from '../../../app/store'
import { fromMaterialJson, Material } from '../../../models/material'
import * as R from '../../../models/recipe'
import {
  getRecipeCategoryName,
  Recipe,
  RecipeCategory,
} from '../../../models/recipe'
import * as SR from '../../../services/recipe'
import {
  CreateRetailRecipeParams,
  CreateRetailRecipePatientParams,
  PayTypeBodyBatch,
  updateItemPayTypeBatch,
} from '../../../services/recipe'
import { remindSaveRecipes, SaveRecipesParams } from '../../../services/remind'
import { fixGroupNumber } from '../../../utils/MaterialUtils'
import { sendCompliancePayload } from '../../../utils/complianceReminder'
import { setRemindDetail } from '../diagnosis/diagnosisSlice'
import { baseDataType } from '../../../services/commodity'
import { notification } from 'antd'

export const PRESCRIPTION_DRUG_LIMIT_CREATE_RECIPE = 'CREATE_RECIPE'
export const PRESCRIPTION_DRUG_LIMIT_RECIPE_FULL = 'RECIPE_FULL'

interface RecipeEditorState {
  recipe?: Recipe
  materials: Material[]
  overdoseReminderList?: any[]
  batchAddVisibel?: boolean // 批量添加弹框
  curePlanListVisible?: boolean
  AddCurePlanModalVisible?: boolean
  planCount?: number // 治疗计划数量
  pageLoading?: boolean
  editPlanId?: string
  // 记录并发的删除明细请求，全部结束后才允许结算或完成诊断
  removingMaterialsRequestCount: number
  // 记录并发的添加药品请求，包括普通、批量和套餐添加
  savingMaterialsRequestCount: number
  // 当前选中的处方 ID，切换处方时记录；用于丢弃晚到的旧处方详情响应，避免右侧表格展示错位
  currentRecipeId?: string
}

const initialState = {
  materials: [],
  overdoseReminderList: [],
  batchAddVisibel: false,
  curePlanListVisible: false,
  AddCurePlanModalVisible: false,
  planCount: 0,
  pageLoading: false,
  editPlanId: '',
  removingMaterialsRequestCount: 0,
  savingMaterialsRequestCount: 0,
  currentRecipeId: '',
} as RecipeEditorState

function reloadRecipeMaterials({
  dispatch,
  getState,
}: {
  dispatch: RootDispatch
  getState: () => RootState
}) {
  const recipe = selectRecipe(getState())
  if (recipe) {
    dispatch(getRecipeDetail(recipe.id))
  }
}

export const createOrUpdateRecipe = createAsyncThunk<
  string,
  Recipe,
  RootThunkApi<string>
>('recipeEditor/createOrUpdateRecipe', async (recipe, api) => {
  return sendAsync(SR.createOrUpdateRecipe(recipe), api).then((data) => {
    reloadRecipeMaterials(api)
    return data?.toString() || ''
  })
})

export const createRetailRecipe = createAsyncThunk<
  Recipe,
  CreateRetailRecipeParams,
  RootThunkApi<Recipe>
>('recipeEditor/createRetailRecipeStatus', async (params, api) => {
  return sendAsync(
    SR.createRetailRecipe(params),
    api
  ).then((data: Record<string, never>) => R.fromJson(data))
})

export const createRetailRecipePatient = createAsyncThunk<
  Recipe,
  CreateRetailRecipePatientParams,
  RootThunkApi<Recipe>
>('recipeEditor/createRetailRecipePatient', async (params, api) => {
  return sendAsync(
    SR.createRetailRecipePatients(params),
    api
  ).then((data: Record<string, never>) => R.fromJson(data))
})

/**
 * 复制处方（/recipe/copy）成功后，触发本地合规分析（整方审核）。
 *
 * 入参取值规则（与后端约定）：
 * - 取接口返回 recipeItemsList 中第一条的 recipeId 作为审核处方；
 * - treatmentId 优先取明细上的，取不到时回落到当前处方所属就诊；
 * - 整方审核不传 recipeItemId，businessType 固定 recipeAnalysis；
 * - 接口报错（errorMsg 非空）、空集合或非数组均不触发。
 *
 * 本地服务未开启、取报文失败等均静默处理，不影响复制处方主流程。
 */
function sendComplianceForCopiedRecipe(
  recipeItemsList: any,
  errorMsg: string | undefined,
  recipe: Recipe | undefined,
  destRecipeId: string | undefined
) {
  // 合规分析属于附加能力，内部任何异常都不允许影响复制处方主流程
  try {
    if (errorMsg) {
      return
    }
    if (!Array.isArray(recipeItemsList) || recipeItemsList.length === 0) {
      return
    }
    const first = recipeItemsList[0] || {}
    const recipeId = first.recipeId ?? destRecipeId
    const treatmentId = first.treatmentId ?? recipe?.treatmentId
    if (!recipeId) {
      return
    }
    if (treatmentId === undefined || treatmentId === null || treatmentId === '') {
      return
    }
    sendCompliancePayload({
      businessType: 'recipeAnalysis',
      treatmentId,
      recipeId,
      isNeedRes: true,
    })
  } catch (e) {
    // do nothing
  }
}

export const copyRecipe = createAsyncThunk<
  void,
  SR.CopyRecipeParams,
  RootThunkApi<void>
>('recipeEditor/copyRecipeStatus', async (params, api) => {
  return sendAsync(SR.copyRecipe(params), api).then((v: any) => {
    reloadRecipeMaterials(api)
    if (v.errorMsg !== '') {
      notification.error({
        message: v.errorMsg,
      })
    }
    // 复制处方成功后，取返回明细中第一条的 recipeId 做整方合规审核
    sendComplianceForCopiedRecipe(
      v?.recipeItemsList,
      v?.errorMsg,
      selectRecipe(api.getState()),
      params?.destRecipeId
    )
    return v?.recipeItemsList
  })
})

export const exchangeList = createAsyncThunk<
  void,
  SR.ExchangeParams,
  RootThunkApi<void>
>('recipeEditor/exchangeList', async (params, api) => {
  return sendAsync(SR.exchangeRecipe(params), api)
})

// 执行单打印内容
export const getPrintZXDData = createAsyncThunk<void, any, RootThunkApi<void>>(
  'recipeEditor/getPrintZXDData',
  async (params, api) => {
    return sendAsync(SR.printZXDData(params), api)
  }
)

export const getRecipeCurePlanDetail = createAsyncThunk<
  [Recipe, Material[]],
  string,
  RootThunkApi<[Recipe, Material[]]>
>('recipeEditor/getRecipeCurePlanDetail', async (param, api) => {
  return sendAsync(SR.getRecipeDetail(param), api).then(
    (data: { records: [Record<string, never>] }) => {
      const items =
        _.chain(data.records?.[0]?.itemList)
          .map((m) => fromMaterialJson(m))
          .sort((a, b) => {
            if (a.groupNumber < b.groupNumber) {
              return -1
            }
            if (a.groupNumber > b.groupNumber) {
              return 1
            }
            return a.sort - b.sort
          })
          .value() || []
      return [R.fromJson(data.records[0]), items] as [Recipe, Material[]]
    }
  )
})

export const getRecipeDetail = createAsyncThunk<
  [Recipe, Material[], any, any[]],
  string,
  RootThunkApi<[Recipe, Material[]]>
>('recipeEditor/getRecipeDetailStatus', async (param, api) => {
  return sendAsync(SR.getRecipeDetail(param), api).then(
    (data: { records: [Record<string, never>] }) => {
      const items =
        _.chain(data.records?.[0]?.itemList)
          .map((m) => fromMaterialJson(m))
          .sort((a, b) => {
            if (a.groupNumber < b.groupNumber) {
              return -1
            }
            if (a.groupNumber > b.groupNumber) {
              return 1
            }
            return a.sort - b.sort
          })
          .value() || []
      // 中药贴敷
      const groupList: any = data.records?.[0]?.groupList || []

      // const path = window.location.pathname // 仅在医嘱处方页面调用
      // path == '/treatment' &&
      //   api.dispatch(
      //     remindSaveRecipesAsync({
      //       recipeName: getRecipeCategoryName(data.records[0].category),
      //       recipeItems: items.length
      //         ? items.map((v) => ({ name: v.name }))
      //         : [],
      //     })
      //   )
      let oneselfAmount = 0 //医嘱处方-自费总价
      let insuranceAmount = 0 //医嘱处方-医保总价
      items.forEach((i: any) => {
        if (i.itemPayType == 0) {
          oneselfAmount = i.drugAmount + oneselfAmount
        } else if (i.itemPayType == 1) {
          insuranceAmount = i.drugAmount + insuranceAmount
        }
      })
      const replaceMakePrice =
        items.find(
          (v) => v.billingCategory == '97' && v?.insuranceCode == 'ZIFEI'
        )?.retailPrice || 0

      const YBReplaceMakePrice =
        items.find(
          (v) => v.billingCategory == '97' && v?.insuranceCode != 'ZIFEI'
        )?.retailPrice || 0
      return [
        R.fromJson({
          ...data.records[0],
          groupList,
          replaceMakePrice,
          YBReplaceMakePrice,
          oneselfAmount,
          insuranceAmount,
        }),
        items,
        data?.records[0]?.uncompletedPlanNum || 0,
        data?.records[0]?.overdoseReminderList || [],
      ] as [Recipe, Material[], any, any[]]
    }
  )
})

export const saveRecipeMaterials = createAsyncThunk<
  any,
  SR.SaveRecipeMaterialsParams,
  RootThunkApi<any>
>('recipeEditor/saveRecipeMaterials', async (params, api) => {
  let v
  try {
    v = await sendAsync(SR.saveRecipeMaterials(params), api, true);
  } catch (e) {
    // 后端命中处方药品种类限制时返回业务状态，前端按 flag 做提示和新增处方处理。
    const limitFlag = e?.data?.flag
    if (
      limitFlag === PRESCRIPTION_DRUG_LIMIT_CREATE_RECIPE ||
      limitFlag === PRESCRIPTION_DRUG_LIMIT_RECIPE_FULL
    ) {
      return e.data
    }
    throw e
  }
  const recipeId = v?.[0]?.recipeId
  if (recipeId) {
    api.dispatch(getRecipeDetail(recipeId))
  }
  return v
  // await sendAsync(SR.saveRecipeMaterials(params), api).then((v) => {
  //   const recipeId = v?.[0]?.recipeId
  //   if (recipeId) {
  //     api.dispatch(getRecipeDetail(recipeId))
  //   }
  // })
})

export const saveRecipeComboMaterials = createAsyncThunk<
  any,
  SR.SaveRecipeMaterialsParams,
  RootThunkApi<any>
>('recipeEditor/saveRecipeComboMaterials', async (params, api) => {
  let v
  try {
    v = await sendAsync(SR.saveRecipeComboMaterials(params), api, true);
  } catch (e) {
    // 套餐保存同样走 batchsave，需要透传处方药品种类限制状态。
    const limitFlag = e?.data?.flag
    if (
      limitFlag === PRESCRIPTION_DRUG_LIMIT_CREATE_RECIPE ||
      limitFlag === PRESCRIPTION_DRUG_LIMIT_RECIPE_FULL
    ) {
      return e.data
    }
    throw e
  }
  const recipeId = v?.[0]?.recipeId
  if (recipeId) {
    api.dispatch(getRecipeDetail(recipeId))
  }
  return v
});

export const createOrUpdateGroupRecipe = createAsyncThunk<
  void,
  SR.GroupRecipeParams,
  RootThunkApi<void>
>('recipeEditor/createOrUpdateGroupRecipe', async (params, api) => {
  return sendAsync(SR.createOrUpdateGroupRecipe(params), api).then((data) => {
    reloadRecipeMaterials(api)
    return data?.toString() || ''
  })
})

// 删除分组
export const deleteGroupRecipe = createAsyncThunk<
  void,
  SR.GroupRecipeParams,
  RootThunkApi<void>
>('recipeEditor/deleteGroupRecipe', async (params, api) => {
  return sendAsync(SR.deleteGroupRecipe(params), api).then((data) => {
    reloadRecipeMaterials(api)
    return data?.toString() || ''
  })
})

export const removeMaterials = createAsyncThunk<void, SR.RemoveMaterialsParams, RootThunkApi>(
  'recipeEditor/removeMaterialsStatus',
  async (params, api) => {
    await sendAsync(SR.removeMaterials(params), api)
    const materials = selectRecipeMaterials(api.getState()).filter(
      (m) => !params?.ids.includes(m.id)
    )
  
    api.dispatch(updateSortAndGroupNumber({ materials: fixGroupNumber(materials), syncFirstItemFUD: params.syncFirstItemFUD }))
  }
)

export interface convertToZifeiParam {
  registrationId?: string
  patientName?: string
  treatmentId?: string
}
export const getInsuranceToSelfpay = createAsyncThunk<
  void,
  convertToZifeiParam,
  RootThunkApi<void>
>('recipeEditor/getInsuranceToSelfpay', async (params, api) => {
  return sendAsync(SR.insuranceToSelfpay(params), api).then(() => {
    reloadRecipeMaterials(api)
  })
})
export const getInsuranceToSelfpayAsync = createAsyncThunk<
  void,
  convertToZifeiParam,
  RootThunkApi<void>
>('recipeEditor/getInsuranceToSelfpay', async (params, api) => {
  return sendAsync(SR.insuranceToSelfpay(params), api)
})
export const updateSortAndGroupNumber = createAsyncThunk<
  void,
  SR.UpdateSortAndGroupNumberParams,
  RootThunkApi<void>
>('recipeEditor/updateSortAndGroupNumberState', async (params, api) => {
  return sendAsync(SR.updateSortAndGroupNumber(params), api).then(() => {
    reloadRecipeMaterials(api)
  })
})

// 医嘱处方-代煎处方信息
export const getReplaceMakeInfo = createAsyncThunk<
  void,
  SR.replaceMakeInfoParams,
  RootThunkApi<void>
>('recipeEditor/getReplaceMakeInfo', async (params, api) => {
  return sendAsync(SR.replaceMakeInfo(params), api)
})

// 医嘱处方-批量变更收费类型
export const updateItemPayTypeBatchAsync = createAsyncThunk<
  void,
  SR.PayTypeBodyBatch,
  RootThunkApi<void>
>('recipeEditor/updateItemPayTypeBatch', async (params, api) => {
  return sendAsync(SR.updateItemPayTypeBatch(params), api)
})

// 医嘱处方-签约
export const replaceMakeInfoAgreementAsync = createAsyncThunk<
  void,
  string,
  RootThunkApi<void>
>('recipeEditor/replaceMakeInfoAgreementAsync', async (params, api) => {
  return sendAsync(SR.replaceMakeInfoAgreement(params), api)
})

// 医嘱处方-代煎设置详情
export const getReplaceMakeSettingDetail = createAsyncThunk<
  void,
  SR.replaceMakeInfoParams,
  RootThunkApi<void>
>('recipeEditor/getReplaceMakeSettingDetail', async (params, api) => {
  return sendAsync(SR.replaceMakeSettingDetail(params), api)
})

// 获取机构支持的代煎方式
export const getTenantPreferencesListAsync = createAsyncThunk<
  any,
  void,
  RootThunkApi<void>
>('recipeEditor/getTenantPreferencesListAsync', async (params, api) => {
  return sendAsync(SR.getTenantPreferencesList(), api)
})

// 设置为默认偏好接口
export const getPreferencesUpgradeAsync = createAsyncThunk<
  void,
  any,
  RootThunkApi<void>
>('recipeEditor/getPreferencesUpgradeAsync', async (params, api) => {
  return sendAsync(SR.getPreferencesUpgrade(params), api)
})

// 医嘱处方-代煎设置保存
export const isSaveReplaceMakeSetting = createAsyncThunk<
  void,
  SR.saveReplaceMakeSettingParams,
  RootThunkApi<void>
>('recipeEditor/isSaveReplaceMakeSetting', async (params, api) => {
  return sendAsync(SR.saveReplaceMakeSetting(params), api)
})

// 医嘱处方-删除代煎设置
export const isdeleteReplaceMakeSetting = createAsyncThunk<
  void,
  SR.deleteReplaceMakeSettingParams,
  RootThunkApi<void>
>('recipeEditor/isdeleteReplaceMakeSetting', async (params, api) => {
  return sendAsync(SR.deleteReplaceMakeSetting(params), api)
})

// 医嘱处方-获取全国位置
export const getAddress = createAsyncThunk<void, string, RootThunkApi<void>>(
  'recipeEditor/getAddress',
  async (params, api) => {
    return sendAsync(SR.address(params), api)
  }
)
// 医嘱处方-获取上一次地址
export const getOldAddress = createAsyncThunk<
  void,
  SR.oldAddressParams,
  RootThunkApi<void>
>('recipeEditor/getOldAddress', async (params, api) => {
  return sendAsync(SR.oldAddress(params), api)
})
// 医嘱处方-获取收货位置
export const getShopAddress = createAsyncThunk<
  void,
  SR.shopAddressParams,
  RootThunkApi<void>
>('recipeEditor/getShopAddress', async (params, api) => {
  return sendAsync(SR.shopAddress(params), api)
})
// 医嘱处方-// 获取电话列表
export const getPhoneList = createAsyncThunk<void, any, RootThunkApi<void>>(
  'recipeEditor/getPhoneList',
  async (params, api) => {
    return sendAsync(SR.phoneList(params), api)
  }
)

// 医嘱处方-获取患者所有地址
export const getPatientAddressListAsync = createAsyncThunk<any, number, RootThunkApi<any>>(
  'recipeEditor/getPatientAddressList',
  async (patientId, api) => {
    return sendAsync(SR.getPatientAddressList(patientId), api)
  }
)

// 医嘱处方-删除患者收货地址
export const removePatientAddressAsync = createAsyncThunk<any, string, RootThunkApi<any>>(
  'recipeEditor/removePatientAddress',
  async (ids, api) => {
    return sendAsync(SR.removePatientAddress(ids), api)
  }
)

// 同步修改用法
export const getBatchSynchronizationRecipeItem = createAsyncThunk<
  void,
  any,
  RootThunkApi<void>
>('recipeEditor/batchSynchronizationRecipeItem', async (params, api) => {
  return sendAsync(SR.batchSynchronizationRecipeItem(params), api)
})

// 优惠券列表
export const getPatientCouponsList = createAsyncThunk<
  void,
  any,
  RootThunkApi<void>
>('recipeEditor/getPhoneList', async (params, api) => {
  return sendAsync(SR.patientCouponsList(params), api)
})

// 优惠券使用
export const getCouponsUse = createAsyncThunk<
  void,
  SR.CouponsUseParams,
  RootThunkApi<void>
>('recipeEditor/getPhoneList', async (params, api) => {
  return sendAsync(SR.CouponsUse(params), api)
})

// 代煎偏好设置
export const getDecoctingPersonalSettingAsync = createAsyncThunk<
  void,
  SR.DecoctingPersonalSettingParams,
  RootThunkApi<void>
>('recipeEditor/getDecoctingPersonalSettingAsync', async (params, api) => {
  return sendAsync(SR.decoctingPersonalSetting(params), api)
})

// 代煎回填偏好设置
export const getDecoctingPersonalDetailAsync = createAsyncThunk<
  any,
  void,
  RootThunkApi<void>
>('recipeEditor/getDecoctingPersonalDetailAsync', async (params, api) => {
  return sendAsync(SR.decoctingPersonalDetail(), api)
})

// 委外代煎通过饮片厂回填偏好设置
export const getBackfillDataAsync = createAsyncThunk<
  void,
  SR.BackfillDataParams,
  RootThunkApi<void>
>('recipeEditor/getBackfillDataAsync', async (params, api) => {
  return sendAsync(SR.backfillData(params), api)
})

// 本地代煎通过回填偏好设置
export const getPotionpreferencesSettingAsync = createAsyncThunk<
  void,
  any,
  RootThunkApi<void>
>('recipeEditor/getPotionpreferencesSettingAsync', async (params, api) => {
  return sendAsync(SR.getPotionpreferencesSetting(params), api)
})
// 事中提醒保存处方时
export const remindSaveRecipesAsync = createAsyncThunk<
  any,
  SaveRecipesParams,
  RootThunkApi
>('recipeEditor/remindSaveMedicalAsync', async (body, api) => {
  return sendAsync(remindSaveRecipes(body), api).then((data) => {
    api.dispatch(setRemindDetail(data))
  })
})

//扫追溯码直接开药
export const getQuicklyPrescribeBySpecialWayFn = createAsyncThunk<
  void,
  SR.PreferencesUpgradeParams,
  RootThunkApi<void>
>('recipeEditor/getQuicklyPrescribeBySpecialWay', async (params, api) => {
  return sendAsync(SR.getQuicklyPrescribeBySpecialWay(params), api)
})


const recipeEditorSlice = createSlice({
  name: 'recipeEditor',
  initialState,
  reducers: {
    resetRecipeEditor: (state) => {
      state.recipe = undefined
      state.materials = []
      state.overdoseReminderList = []
      state.currentRecipeId = ''
    },
    setCurrentRecipeId: (state, action) => {
      state.currentRecipeId = action.payload
    },
    setMaterials: (state, action) => {
      state.materials = action.payload
    },
    setOverdoseReminderList: (state, action) => {
      state.overdoseReminderList = action.payload
    },
    setBatchAddVisibel: (state, action) => {
      state.batchAddVisibel = action.payload
    },
    setAddCurePlanModalVisible: (state, action) => {
      state.AddCurePlanModalVisible = action.payload
    },
    setEditPlanId: (state, { payload }) => {
      state.editPlanId = payload
    },
    setCurePlanListVisible: (state, action) => {
      state.curePlanListVisible = action.payload
    },
    setPlanCount: (state, action) => {
      state.planCount = action.payload
    },
    setPageLoading: (state, action) => {
      state.pageLoading = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveRecipeMaterials.pending, (state) => {
      state.savingMaterialsRequestCount += 1
    })
    builder.addCase(saveRecipeMaterials.fulfilled, (state) => {
      state.savingMaterialsRequestCount = Math.max(
        0,
        state.savingMaterialsRequestCount - 1
      )
    })
    builder.addCase(saveRecipeMaterials.rejected, (state) => {
      state.savingMaterialsRequestCount = Math.max(
        0,
        state.savingMaterialsRequestCount - 1
      )
    })
    builder.addCase(saveRecipeComboMaterials.pending, (state) => {
      state.savingMaterialsRequestCount += 1
    })
    builder.addCase(saveRecipeComboMaterials.fulfilled, (state) => {
      state.savingMaterialsRequestCount = Math.max(
        0,
        state.savingMaterialsRequestCount - 1
      )
    })
    builder.addCase(saveRecipeComboMaterials.rejected, (state) => {
      state.savingMaterialsRequestCount = Math.max(
        0,
        state.savingMaterialsRequestCount - 1
      )
    })
    builder.addCase(removeMaterials.pending, (state) => {
      state.removingMaterialsRequestCount += 1
    })
    builder.addCase(removeMaterials.fulfilled, (state) => {
      state.removingMaterialsRequestCount = Math.max(
        0,
        state.removingMaterialsRequestCount - 1
      )
    })
    builder.addCase(removeMaterials.rejected, (state) => {
      state.removingMaterialsRequestCount = Math.max(
        0,
        state.removingMaterialsRequestCount - 1
      )
    })
    builder.addCase(getRecipeDetail.fulfilled, (state, action) => {
      // 只应用「当前选中处方」的详情，晚到的旧处方响应直接丢弃，避免右侧表格展示错位
      if (
        state.currentRecipeId &&
        String(action.meta.arg) !== String(state.currentRecipeId)
      ) {
        return
      }
      state.recipe = action.payload[0]
      state.materials = action.payload[1]
      state.planCount = action.payload[2]
      state.overdoseReminderList = action.payload[3]
      state.pageLoading = false
    })
    builder.addCase(createRetailRecipe.fulfilled, (state, action) => {
      state.recipe = action.payload
    })
    // builder.addCase(saveRecipeMaterials.fulfilled, (state, action) => {
    //   const oldMaterials = JSON.parse(JSON.stringify(state.materials))
    //   const item: any = action.payload
    //   if (oldMaterials.find((v: any) => v.id === String(item[0].id))) {
    //     state.materials = oldMaterials.map((v: any) => {
    //       if (v.id === String(item[0].id)) {
    //         return item[0]
    //       }
    //       return v
    //     })
    //   } else {
    //     state.materials = oldMaterials.concat(action.payload)
    //   }
    // })
  },
})

export const {
  resetRecipeEditor,
  setCurrentRecipeId,
  setMaterials,
  setOverdoseReminderList,
  setBatchAddVisibel,
  setCurePlanListVisible,
  setAddCurePlanModalVisible,
  setPlanCount,
  setEditPlanId,
  setPageLoading,
} = recipeEditorSlice.actions

export const selectRecipe = (state: RootState): Recipe | undefined =>
  state.recipeEditor.recipe

export const selectRecipeMaterials = (state: RootState): Material[] =>
  state.recipeEditor.materials

export const selectOverdoseReminderList = (state: RootState) =>
  state.recipeEditor.overdoseReminderList

export const selectBatchAddVisibel = (state: RootState) =>
  state.recipeEditor.batchAddVisibel

export const selectAddCurePlanModalVisible = (state: RootState) =>
  state.recipeEditor.AddCurePlanModalVisible

export const selectCurePlanListVisible = (state: RootState) =>
  state.recipeEditor.curePlanListVisible

export const selectPlanCount = (state: RootState) =>
  state.recipeEditor.planCount

export const selectPageLoading = (state: RootState) =>
  state.recipeEditor.pageLoading

export const selectIsRemovingMaterials = (state: RootState): boolean =>
  state.recipeEditor.removingMaterialsRequestCount > 0

export const selectIsSavingMaterials = (state: RootState): boolean =>
  state.recipeEditor.savingMaterialsRequestCount > 0

export const selectsEtEditPlanId = (state: RootState) =>
  state.recipeEditor.editPlanId

export default recipeEditorSlice.reducer
